import React, { useState } from "react";
import { useCreateHabit, useCreateHabitGroup } from "../hooks/queries";
import { TerminalLine } from "./terminal/TerminalLine";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  existingGroups: Array<{ id: string; name: string }>;
  defaultDate?: string;
  initialIsOneOff?: boolean;
}

export function HabitForm({ isOpen, onClose, existingGroups, defaultDate, initialIsOneOff = false }: Props) {
  const createHabit = useCreateHabit();
  const createGroup = useCreateHabitGroup();

  const [name, setName] = useState("");
  const [type, setType] = useState<"boolean" | "counter" | "timer">("boolean");
  const [icon, setIcon] = useState("·");
  const [targetValue, setTargetValue] = useState(1);
  const [xpValue, setXpValue] = useState(10);
  const [isOneOff, setIsOneOff] = useState(initialIsOneOff);
  const [targetDate, setTargetDate] = useState(defaultDate || "");
  const [timeOfDay, setTimeOfDay] = useState("morning");

  // Group selection
  const [groupId, setGroupId] = useState("");
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupIcon, setNewGroupIcon] = useState("◦");
  const [newGroupColor, setNewGroupColor] = useState("#f59e0b");

  const [errorMsg, setErrorMsg] = useState("");

  if (!isOpen) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg("");

    let activeGroupId = groupId;

    try {
      if (showNewGroupForm || !groupId) {
        if (!newGroupName.trim()) {
          setErrorMsg("group name is required");
          return;
        }
        // Create group first
        const groupRes = await createGroup.mutateAsync({
          name: newGroupName.trim(),
          icon: newGroupIcon.trim() || "◦",
          color: newGroupColor,
        });
        activeGroupId = groupRes.id;
      }

      if (!name.trim()) {
        setErrorMsg("habit name is required");
        return;
      }

      await createHabit.mutateAsync({
        groupId: activeGroupId,
        name: name.trim(),
        type,
        icon: icon.trim() || "·",
        targetValue,
        xpValue,
        isOneOff,
        targetDate: isOneOff ? targetDate : null,
        timeOfDay,
      });

      // Reset
      setName("");
      setType("boolean");
      setIcon("·");
      setTargetValue(1);
      setXpValue(10);
      setIsOneOff(false);
      setTargetDate(defaultDate || "");
      setTimeOfDay("morning");
      setGroupId("");
      setShowNewGroupForm(false);
      setNewGroupName("");
      setNewGroupIcon("◦");
      setNewGroupColor("#f59e0b");
      onClose();
    } catch (err: any) {
      setErrorMsg(err?.message || "failed to create habit");
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        className="shukan-input"
        style={{
          width: "100%",
          maxWidth: "32rem",
          backgroundColor: "var(--bg-elevated)",
          border: "2px solid var(--accent)",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <TerminalLine prompt accent>
          <strong>create new habit</strong>
        </TerminalLine>

        {errorMsg && (
          <div style={{ color: "var(--danger)", fontSize: "0.85rem" }}>
            ! {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Group Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ select group</label>
            {!showNewGroupForm ? (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <select
                  className="shukan-input"
                  style={{ flex: 1, padding: "0.4rem" }}
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value="">-- select existing group --</option>
                  {existingGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="shukan-btn"
                  onClick={() => setShowNewGroupForm(true)}
                  style={{ fontSize: "0.8rem" }}
                >
                  + new group
                </button>
              </div>
            ) : (
              <div style={{ border: "1px dashed var(--border)", padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "0.75rem", color: "var(--accent)" }}>New Group Details</span>
                  <button
                    type="button"
                    className="shukan-btn"
                    onClick={() => setShowNewGroupForm(false)}
                    style={{ fontSize: "0.7rem", padding: "0.1rem 0.3rem" }}
                  >
                    cancel
                  </button>
                </div>
                <input
                  className="shukan-input"
                  placeholder="group name (e.g. Health, Coding)"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                />
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    className="shukan-input"
                    placeholder="icon (e.g. ◦)"
                    value={newGroupIcon}
                    onChange={(e) => setNewGroupIcon(e.target.value)}
                    style={{ width: "4rem" }}
                  />
                  <input
                    type="color"
                    className="shukan-input"
                    value={newGroupColor}
                    onChange={(e) => setNewGroupColor(e.target.value)}
                    style={{ width: "4rem", height: "2rem", padding: 0 }}
                  />
                  <span style={{ fontSize: "0.75rem", alignSelf: "center", color: "var(--fg-dim)" }}>color swatch</span>
                </div>
              </div>
            )}
          </div>

          {/* Habit Details */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ habit name</label>
            <input
              className="shukan-input"
              placeholder="e.g. Read 10 pages, Gym"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ tracking type</label>
              <select
                className="shukan-input"
                style={{ padding: "0.4rem" }}
                value={isOneOff ? "one-off" : "daily"}
                onChange={(e) => {
                  const val = e.target.value === "one-off";
                  setIsOneOff(val);
                }}
              >
                <option value="daily">daily recurring (remains tomorrow)</option>
                <option value="one-off">one-off task (add to list for a single day)</option>
              </select>
            </div>
            {isOneOff && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", width: "10rem" }}>
                <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ target date</label>
                <input
                  type="date"
                  className="shukan-input"
                  style={{ padding: "0.3rem" }}
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ type</label>
              <select
                className="shukan-input"
                style={{ padding: "0.4rem" }}
                value={type}
                onChange={(e) => setType(e.target.value as any)}
              >
                <option value="boolean">boolean (done/not done)</option>
                <option value="counter">counter (integer target)</option>
                <option value="timer">timer (minutes target)</option>
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", width: "8rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ period</label>
              <select
                className="shukan-input"
                style={{ padding: "0.4rem" }}
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
              >
                <option value="morning">☀ Morning</option>
                <option value="afternoon">☼ Afternoon</option>
                <option value="evening">☾ Night</option>
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", width: "5rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ icon</label>
              <input
                className="shukan-input"
                placeholder="·"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "1rem" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ target value</label>
              <input
                type="number"
                min={1}
                className="shukan-input"
                value={targetValue}
                onChange={(e) => setTargetValue(parseInt(e.target.value) || 1)}
                disabled={type === "boolean"}
              />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ xp reward</label>
              <input
                type="number"
                min={0}
                className="shukan-input"
                value={xpValue}
                onChange={(e) => setXpValue(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          <div style={{ marginTop: "1rem", display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
            <button
              type="button"
              className="shukan-btn"
              onClick={onClose}
            >
              cancel
            </button>
            <button
              type="submit"
              className="shukan-btn shukan-btn-primary"
            >
              $ save habit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
